import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  FileSpreadsheet, Loader2, Download, ChevronDown, Hash,
  ArrowUpRight, ArrowDownLeft, TrendingUp, IndianRupee, Filter
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FeatureGate } from "../components/FeatureGate";
import { formatIndianDate } from "../utils/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (a) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(a || 0);

function getCurrentFY() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month < 4) return `${year - 1}-${String(year).slice(2)}`;
  return `${year}-${String(year + 1).slice(2)}`;
}

function getFYOptions() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const options = [];
  for (let y = currentYear; y >= currentYear - 3; y--) {
    options.push(`${y}-${String(y + 1).slice(2)}`);
  }
  return options;
}

function getMonthOptions(fy) {
  if (!fy) return [];
  const startYear = parseInt(fy.split("-")[0]);
  const months = [];
  for (let m = 4; m <= 12; m++) {
    months.push({ value: `${startYear}-${String(m).padStart(2, "0")}`, label: new Date(startYear, m - 1).toLocaleString("en-IN", { month: "long", year: "numeric" }) });
  }
  for (let m = 1; m <= 3; m++) {
    months.push({ value: `${startYear + 1}-${String(m).padStart(2, "0")}`, label: new Date(startYear + 1, m - 1).toLocaleString("en-IN", { month: "long", year: "numeric" }) });
  }
  return months;
}

function StatCard({ label, value, sub, icon: Icon, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    indigo: "bg-indigo-50 text-indigo-600",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

// ==================== GSTR-1 TAB ====================
function GSTR1Tab({ companies, fy, month, companyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandB2B, setExpandB2B] = useState(true);
  const [expandB2CS, setExpandB2CS] = useState(false);

  useEffect(() => { fetchData(); }, [fy, month, companyId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyId && companyId !== "all") params.company_id = companyId;
      if (month) params.month = month;
      else if (fy) params.fy = fy;
      const res = await axios.get(`${API}/reports/gstr1`, { params, withCredentials: true });
      setData(res.data);
    } catch { toast.error("Failed to load GSTR-1"); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return null;

  const s = data.summary;

  const handleExport = () => {
    const params = new URLSearchParams();
    if (companyId && companyId !== "all") params.set("company_id", companyId);
    if (month) params.set("month", month);
    else if (fy) params.set("fy", fy);
    window.open(`${API}/reports/export/gstr1?${params.toString()}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="gstr1-tab">
      {/* Summary Cards */}
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
        <StatCard label="Total Invoices" value={s.total_invoices} icon={FileSpreadsheet} color="blue" />
        <StatCard label="Net Sales" value={fmt(s.total_taxable_value)} icon={IndianRupee} color="emerald" />
        <StatCard label="Total Tax" value={fmt(s.total_tax)} sub={`CGST: ${fmt(s.total_cgst)} | SGST: ${fmt(s.total_sgst)} | IGST: ${fmt(s.total_igst)}`} icon={TrendingUp} color="amber" />
        <StatCard label="Invoice Value" value={fmt(s.total_invoice_value)} icon={ArrowUpRight} color="indigo" />
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} className="ml-3 flex-shrink-0" data-testid="export-gstr1-btn">
          <Download className="w-4 h-4 mr-1.5" /> Export Excel
        </Button>
      </div>

      {/* B2B Section */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <button
          onClick={() => setExpandB2B(!expandB2B)}
          className="w-full flex items-center justify-between p-5"
          data-testid="b2b-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <ArrowUpRight className="w-4 h-4 text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-slate-900">B2B Supplies</h3>
              <p className="text-xs text-slate-500">Invoices to registered businesses ({data.b2b_count})</p>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandB2B ? "rotate-180" : ""}`} />
        </button>
        {expandB2B && (
          <div className="px-5 pb-5">
            {data.b2b.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No B2B invoices in this period</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 text-xs">
                      <th className="text-left py-2 font-medium">Invoice #</th>
                      <th className="text-left py-2 font-medium">Date</th>
                      <th className="text-left py-2 font-medium">Customer</th>
                      <th className="text-left py-2 font-medium">GSTIN</th>
                      <th className="text-right py-2 font-medium">Taxable</th>
                      <th className="text-right py-2 font-medium">CGST</th>
                      <th className="text-right py-2 font-medium">SGST</th>
                      <th className="text-right py-2 font-medium">IGST</th>
                      <th className="text-right py-2 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.b2b.map(inv => (
                      <tr key={inv.invoice_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 font-mono text-xs text-slate-700">{inv.invoice_number}</td>
                        <td className="py-2 text-slate-600">{formatIndianDate(inv.invoice_date)}</td>
                        <td className="py-2 font-medium text-slate-900">{inv.customer_name}</td>
                        <td className="py-2 font-mono text-xs text-slate-600">{inv.customer_gst}</td>
                        <td className="py-2 text-right text-slate-600">{fmt(inv.taxable_value)}</td>
                        <td className="py-2 text-right text-slate-600">{fmt(inv.cgst)}</td>
                        <td className="py-2 text-right text-slate-600">{fmt(inv.sgst)}</td>
                        <td className="py-2 text-right text-slate-600">{fmt(inv.igst)}</td>
                        <td className="py-2 text-right font-medium text-slate-900">{fmt(inv.invoice_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* B2CS Section */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <button
          onClick={() => setExpandB2CS(!expandB2CS)}
          className="w-full flex items-center justify-between p-5"
          data-testid="b2cs-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <ArrowDownLeft className="w-4 h-4 text-amber-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-slate-900">B2C Supplies</h3>
              <p className="text-xs text-slate-500">Invoices to unregistered buyers ({data.b2cs_count})</p>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandB2CS ? "rotate-180" : ""}`} />
        </button>
        {expandB2CS && (
          <div className="px-5 pb-5">
            {data.b2cs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No B2C invoices in this period</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 text-xs">
                      <th className="text-left py-2 font-medium">Invoice #</th>
                      <th className="text-left py-2 font-medium">Date</th>
                      <th className="text-left py-2 font-medium">Customer</th>
                      <th className="text-right py-2 font-medium">Taxable</th>
                      <th className="text-right py-2 font-medium">Tax</th>
                      <th className="text-right py-2 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.b2cs.map(inv => (
                      <tr key={inv.invoice_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 font-mono text-xs text-slate-700">{inv.invoice_number}</td>
                        <td className="py-2 text-slate-600">{formatIndianDate(inv.invoice_date)}</td>
                        <td className="py-2 font-medium text-slate-900">{inv.customer_name}</td>
                        <td className="py-2 text-right text-slate-600">{fmt(inv.taxable_value)}</td>
                        <td className="py-2 text-right text-slate-600">{fmt(inv.total_tax)}</td>
                        <td className="py-2 text-right font-medium text-slate-900">{fmt(inv.invoice_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== GSTR-3B TAB ====================
function GSTR3BTab({ companies, month, companyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [month, companyId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyId && companyId !== "all") params.company_id = companyId;
      if (month) params.month = month;
      const res = await axios.get(`${API}/reports/gstr3b`, { params, withCredentials: true });
      setData(res.data);
    } catch { toast.error("Failed to load GSTR-3B"); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return null;

  const { output_tax, input_tax_credit, net_payable } = data;

  const handleExport3B = () => {
    const params = new URLSearchParams();
    if (companyId && companyId !== "all") params.set("company_id", companyId);
    if (month) params.set("month", month);
    window.open(`${API}/reports/export/gstr3b?${params.toString()}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="gstr3b-tab">
      {/* Net Payable Hero */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-1">Net GST Payable</p>
            <p className="text-3xl font-bold">{fmt(net_payable.total)}</p>
            <div className="flex gap-6 mt-3 text-sm">
              <span className="text-slate-300">CGST: {fmt(net_payable.cgst)}</span>
              <span className="text-slate-300">SGST: {fmt(net_payable.sgst)}</span>
              <span className="text-slate-300">IGST: {fmt(net_payable.igst)}</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport3B} className="border-slate-600 text-slate-200 hover:bg-slate-700" data-testid="export-gstr3b-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Output Tax */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="output-tax-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
              <ArrowUpRight className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Output Tax Liability</h3>
              <p className="text-xs text-slate-500">{output_tax.invoice_count} sales invoice{output_tax.invoice_count !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Net Sales</span><span className="font-medium">{fmt(output_tax.taxable_value)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">CGST</span><span>{fmt(output_tax.cgst)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">SGST</span><span>{fmt(output_tax.sgst)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">IGST</span><span>{fmt(output_tax.igst)}</span></div>
            <div className="flex justify-between pt-2 border-t border-slate-100 font-bold">
              <span>Total Output Tax</span><span className="text-red-600">{fmt(output_tax.total)}</span>
            </div>
          </div>
        </div>

        {/* Input Tax Credit */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="input-tax-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <ArrowDownLeft className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Input Tax Credit (ITC)</h3>
              <p className="text-xs text-slate-500">{input_tax_credit.invoice_count} purchase invoice{input_tax_credit.invoice_count !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Net Purchases</span><span className="font-medium">{fmt(input_tax_credit.taxable_value)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">CGST</span><span>{fmt(input_tax_credit.cgst)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">SGST</span><span>{fmt(input_tax_credit.sgst)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">IGST</span><span>{fmt(input_tax_credit.igst)}</span></div>
            <div className="flex justify-between pt-2 border-t border-slate-100 font-bold">
              <span>Total ITC</span><span className="text-emerald-600">{fmt(input_tax_credit.total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== HSN SUMMARY TAB ====================
function HSNSummaryTab({ companies, fy, month, companyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceType, setInvoiceType] = useState("sales");

  useEffect(() => { fetchData(); }, [fy, month, companyId, invoiceType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { invoice_type: invoiceType };
      if (companyId && companyId !== "all") params.company_id = companyId;
      if (month) params.month = month;
      else if (fy) params.fy = fy;
      const res = await axios.get(`${API}/reports/hsn-summary`, { params, withCredentials: true });
      setData(res.data);
    } catch { toast.error("Failed to load HSN summary"); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="hsn-summary-tab">
      {/* Type toggle + Export */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={invoiceType === "sales" ? "default" : "outline"}
            onClick={() => setInvoiceType("sales")}
            className={invoiceType === "sales" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
            size="sm"
            data-testid="hsn-sales-btn"
          >
            Sales
          </Button>
          <Button
            variant={invoiceType === "purchase" ? "default" : "outline"}
            onClick={() => setInvoiceType("purchase")}
            className={invoiceType === "purchase" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
            size="sm"
            data-testid="hsn-purchase-btn"
          >
            Purchases
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={() => {
          const params = new URLSearchParams({ invoice_type: invoiceType });
          if (companyId && companyId !== "all") params.set("company_id", companyId);
          if (month) params.set("month", month);
          else if (fy) params.set("fy", fy);
          window.open(`${API}/reports/export/hsn?${params.toString()}`, "_blank");
        }} data-testid="export-hsn-btn">
          <Download className="w-4 h-4 mr-1.5" /> Export Excel
        </Button>
      </div>

      {data.items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
          <Hash className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No HSN/SAC data for this period</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500">
                  <th className="text-left py-3 px-4 font-medium">HSN/SAC</th>
                  <th className="text-left py-3 px-4 font-medium">Description</th>
                  <th className="text-right py-3 px-4 font-medium">Qty</th>
                  <th className="text-right py-3 px-4 font-medium">Net Sales</th>
                  <th className="text-right py-3 px-4 font-medium">CGST</th>
                  <th className="text-right py-3 px-4 font-medium">SGST</th>
                  <th className="text-right py-3 px-4 font-medium">IGST</th>
                  <th className="text-right py-3 px-4 font-medium">Total Tax</th>
                  <th className="text-right py-3 px-4 font-medium">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2.5 px-4 font-mono font-medium text-slate-900">{item.hsn_code}</td>
                    <td className="py-2.5 px-4 text-slate-600 max-w-[200px] truncate">{item.description || "—"}</td>
                    <td className="py-2.5 px-4 text-right text-slate-600">{item.quantity}</td>
                    <td className="py-2.5 px-4 text-right text-slate-700 font-medium">{fmt(item.taxable_value)}</td>
                    <td className="py-2.5 px-4 text-right text-slate-600">{fmt(item.cgst)}</td>
                    <td className="py-2.5 px-4 text-right text-slate-600">{fmt(item.sgst)}</td>
                    <td className="py-2.5 px-4 text-right text-slate-600">{fmt(item.igst)}</td>
                    <td className="py-2.5 px-4 text-right text-slate-700 font-medium">{fmt(item.total_tax)}</td>
                    <td className="py-2.5 px-4 text-right font-bold text-slate-900">{fmt(item.total_value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-bold text-sm">
                  <td className="py-3 px-4" colSpan={2}>Total</td>
                  <td className="py-3 px-4 text-right">{data.totals.quantity}</td>
                  <td className="py-3 px-4 text-right">{fmt(data.totals.taxable_value)}</td>
                  <td className="py-3 px-4 text-right">{fmt(data.totals.cgst)}</td>
                  <td className="py-3 px-4 text-right">{fmt(data.totals.sgst)}</td>
                  <td className="py-3 px-4 text-right">{fmt(data.totals.igst)}</td>
                  <td className="py-3 px-4 text-right">{fmt(data.totals.total_tax)}</td>
                  <td className="py-3 px-4 text-right">{fmt(data.totals.total_value)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
const TABS = [
  { id: "gstr1", label: "GSTR-1", sub: "Outward Supplies" },
  { id: "gstr3b", label: "GSTR-3B", sub: "Summary Return" },
  { id: "hsn", label: "HSN Summary", sub: "HSN/SAC Wise" },
];

export default function GSTReportsPage() {
  const [tab, setTab] = useState("gstr1");
  const [companies, setCompanies] = useState([]);
  const [fy, setFy] = useState(getCurrentFY());
  const [month, setMonth] = useState("");
  const [companyId, setCompanyId] = useState("all");

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then(res => setCompanies(res.data.companies || []))
      .catch(() => {});
  }, []);

  const monthOptions = useMemo(() => getMonthOptions(fy), [fy]);

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="gst-reports-page">
      <FeatureGate feature="gst_reports">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GST Reports</h1>
            <p className="text-sm text-slate-500">Generate GSTR-1, GSTR-3B, and HSN summaries for GST filing</p>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <Filter className="w-4 h-4 text-slate-400 hidden sm:block" />
            <Select value={fy} onValueChange={v => { setFy(v); setMonth(""); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-fy"><SelectValue placeholder="Financial Year" /></SelectTrigger>
              <SelectContent>
                {getFYOptions().map(f => <SelectItem key={f} value={f}>FY {f}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={month || "all"} onValueChange={v => setMonth(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-52" data-testid="filter-month"><SelectValue placeholder="All Months" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {companies.length > 1 && (
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-company"><SelectValue placeholder="All Companies" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                tab === t.id
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-100 hover:bg-slate-50"
              }`}
              data-testid={`tab-${t.id}`}
            >
              <span className="font-bold">{t.label}</span>
              <span className={`ml-1.5 text-xs ${tab === t.id ? "text-blue-200" : "text-slate-400"}`}>{t.sub}</span>
            </button>
          ))}
        </div>

        {/* Tab Content — pass filter state as props so child tabs reuse them */}
        {tab === "gstr1" && <GSTR1Tab companies={companies} fy={fy} month={month} companyId={companyId} />}
        {tab === "gstr3b" && <GSTR3BTab companies={companies} month={month} companyId={companyId} />}
        {tab === "hsn" && <HSNSummaryTab companies={companies} fy={fy} month={month} companyId={companyId} />}
      </div>
      </FeatureGate>
    </div>
  );
}
